import { useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { DueDatePicker } from './DueDatePicker';

export interface QuickAddTaskProps {
  onAdd: (task: { title: string; dueDate: string | null }) => void;
  todayKey: string;
  eventStartAt?: Date | string | null;
  /** Templates carry offsets, not dates — the date picker is left out. */
  withDate?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

/**
 * Type a task and press Enter. The field stays focused and empties, so a run
 * of tasks goes in without touching the mouse; a due date is one tap away and
 * applies to that one task only.
 */
export function QuickAddTask({
  onAdd,
  todayKey,
  eventStartAt,
  withDate = true,
  placeholder = 'Add a task…',
  autoFocus = false,
}: QuickAddTaskProps) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    onAdd({ title: trimmed, dueDate: dueDate || null });
    setTitle('');
    setDueDate('');
    inputRef.current?.focus();
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="flex items-center gap-2 px-3 py-1.5"
    >
      <Plus size={18} className="shrink-0 text-gray-400" aria-hidden />
      <input
        ref={inputRef}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setTitle('');
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        maxLength={500}
        className="flex-1 min-w-0 py-1.5 text-sm bg-transparent border-0 border-b border-transparent focus:border-primary-400 focus:outline-none focus:ring-0 placeholder:text-gray-400"
      />
      {withDate && (
        <DueDatePicker
          value={dueDate}
          onChange={setDueDate}
          todayKey={todayKey}
          eventStartAt={eventStartAt}
          placeholder="No date"
          className="py-1 text-xs"
        />
      )}
      <button
        type="submit"
        disabled={!title.trim()}
        className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40"
      >
        Add
      </button>
    </form>
  );
}
